/// <reference path="../../../../node_modules/@arcgis/core/interfaces.d.ts" />

import type { VectorTileLayerProperties } from '@arcgis/core/layers/VectorTileLayer';
import type { SpriteInfo } from '@arcgis/core/layers/support/types';
import MapboxLayer from '@Map/layers/mapbox/MapboxLayer';
import SymbolLoader, {
	type BufferImage as SymbolBufferImage,
} from '@Map/symbols/SymbolLoader';
import icons from '@Map/symbols/icons';
import MapView from '@arcgis/core/views/MapView';

import { setVectorTileLayerStyleLayerIfChanged } from './setVectorTileLayerStyleLayerIfChanged';
import {
	colorExpressionToIconImageExpression,
	replaceColorInIconImageExpr,
} from './colorExpressions';

/**
 * Finds a layer in the Esri map that supports renderer-based styling.
 * Returns null for VectorTileLayer (no renderer property — skip silently).
 */
export function findRenderableLayer(
	view: __esri.MapView,
	layerId: string,
): __esri.GeoJSONLayer | __esri.FeatureLayer | null {
	const layer = view.map?.findLayerById(layerId);
	if (layer && 'renderer' in layer) {
		return layer as __esri.GeoJSONLayer | __esri.FeatureLayer;
	}
	return null;
}

/**
 * Converts a Mapbox zoom level to an Esri map scale value.
 * Web Mercator approximation: scale = 591657527.591555 / 2^zoom
 */
export function zoomToScale(zoom: number): number {
	return 591657527.591555 / Math.pow(2, zoom);
}

/**
 * Appends the Mapbox-style selected sprite suffix to an icon id when missing.
 * @see VectorTileStyleToEsriRenderer sprite id packing
 */
export function appendSelectedSuffixToIconString(
	iconId: string,
	selectedColor: string = MapboxLayer.SELECTED_COLOR,
): string {
	if (iconId.includes('-selected')) {
		return iconId;
	}
	if (iconId.startsWith(SymbolLoader.namespace) && iconId.includes('~')) {
		return `${iconId}-selected~${selectedColor}`;
	}
	if (iconId.startsWith('~')) {
		const nobg = SymbolLoader.disableBackgroundPosfix;
		if (iconId.endsWith(nobg)) {
			const base = iconId.slice(0, -nobg.length);
			return `${base}-selected~${selectedColor}${nobg}`;
		}
		return `${iconId}-selected~${selectedColor}`;
	}
	return iconId;
}

/**
 * Rewrites `layout['icon-image']` so sprite ids include the selected variant.
 */
export function applySelectedIconImageSuffix(
	iconImage: unknown,
	selectedColor: string = MapboxLayer.SELECTED_COLOR,
): unknown {
	if (typeof iconImage === 'string') {
		return appendSelectedSuffixToIconString(iconImage, selectedColor);
	}
	if (!Array.isArray(iconImage)) {
		return iconImage;
	}
	const head = iconImage[0];
	if (head === 'concat' && iconImage.length >= 4) {
		const last = iconImage[iconImage.length - 1];
		if (typeof last === 'string') {
			return [
				...iconImage.slice(0, -1),
				appendSelectedSuffixToIconString(last, selectedColor),
			];
		}
	}
	if (head === 'case') {
		return iconImage.map((item, index) =>
			index === 0
				? item
				: applySelectedIconImageSuffix(item, selectedColor),
		);
	}
	return iconImage.map(item =>
		applySelectedIconImageSuffix(item, selectedColor),
	);
}

// ---------------------------------------------------------------------------
// VTL helpers — shared by all style-layer mutation utilities
// ---------------------------------------------------------------------------

/**
 * Finds and returns the loaded VectorTileLayer that owns a style layer with
 * the given `layerId`, or `null` if none is found.
 */
export function findOwnerVtl(view: MapView, layerId: string): any | null {
	for (const layer of view.map?.layers?.toArray() ?? []) {
		if (!('getStyleLayer' in layer && 'setStyleLayer' in layer)) continue;
		const vtl = layer as any;
		if (!vtl?.loaded) continue;
		if (vtl.getStyleLayer?.(layerId)) return vtl;
	}
	return null;
}

/**
 * Returns the canonical style-layer IDs for a logical layer:
 * the base ID plus its selected and highlight variants.
 */
function vtlStyleVariantIds(layerId: string): string[] {
	return [
		layerId,
		`${layerId}-selected`,
		`${layerId}-highlight-background`,
		`${layerId}-highlight-foreground`,
	];
}

// ---------------------------------------------------------------------------

/**
 * Returns the color suffix already baked into the VectorTileLayer style
 * layer's icon-image (e.g. `'007ca0'` from `'inno:manhole~007ca0'` or from
 * `['concat', 'inno:', ['get', 'icon'], '~007ca0']`).
 *
 * The sprite is built from the original style layers, so icon keys must use
 * this same suffix. Returns undefined if no VTL style layer is found.
 */
export function getVtlIconSpriteColor(
	view: __esri.MapView,
	layerId: string,
): string | undefined {
	const mapLayers = view.map?.layers?.toArray() ?? [];
	for (const layer of mapLayers) {
		if (!('getStyleLayer' in layer && 'setStyleLayer' in layer)) continue;
		const vtl = layer as any;
		if (!vtl.loaded) continue;
		const styleLayer = vtl.getStyleLayer?.(layerId);
		if (!styleLayer) continue;
		const iconImage = styleLayer.layout?.['icon-image'];
		if (!iconImage) return undefined;
		// Static string: 'inno:manhole~007ca0'
		if (typeof iconImage === 'string' && iconImage.includes('~')) {
			return iconImage.split('~').pop();
		}
		// Concat expression: ['concat', 'inno:', ['get', 'icon'], '~007ca0']
		if (Array.isArray(iconImage)) {
			const postfix = (iconImage as unknown[]).find(
				(e): e is string => typeof e === 'string' && e.startsWith('~'),
			);
			if (postfix) return postfix.slice(1);
		}
		return undefined;
	}
	return undefined;
}

/**
 * Updates `layout['icon-image']` on a VectorTileLayer style layer (and its
 * `-selected` companion). The initial sprite built from the tile-service style
 * already contains all bundled icons (expanded from the dynamic concat
 * expression), so the new icon is available in the sprite without a rebuild.
 * Calling setSpriteSource here would race with EsriLayerManager's 1500ms
 * backup call and cause icons to flicker off.
 *
 * Returns true if the target style layer was found and updated.
 */
export function updateVectorTileLayerIconImage(
	view: __esri.MapView,
	layerId: string,
	newSymbolId: string,
): boolean {
	const mapLayers = view.map?.layers?.toArray() ?? [];
	for (const layer of mapLayers) {
		if (!('getStyleLayer' in layer && 'setStyleLayer' in layer)) continue;
		const vtl = layer as any;
		if (!vtl.loaded) continue;
		const styleLayer = vtl.getStyleLayer?.(layerId);
		if (!styleLayer) continue;

		const selectedSymbolId = appendSelectedSuffixToIconString(newSymbolId);

		setVectorTileLayerStyleLayerIfChanged(
			vtl,
			{
				...styleLayer,
				layout: {
					...(styleLayer.layout ?? {}),
					'icon-image': newSymbolId,
				},
			},
			{ source: 'updateVectorTileLayerIconImage' },
		);

		const selectedLayerId = `${layerId}-selected`;
		const selectedStyleLayer = vtl.getStyleLayer?.(selectedLayerId);
		if (selectedStyleLayer) {
			setVectorTileLayerStyleLayerIfChanged(
				vtl,
				{
					...selectedStyleLayer,
					layout: {
						...(selectedStyleLayer.layout ?? {}),
						'icon-image': selectedSymbolId,
					},
				},
				{ source: 'updateVectorTileLayerIconImage' },
			);
		}

		return true;
	}
	return false;
}

/**
 * Updates paint properties on a Mapbox GL style layer that lives inside a
 * VectorTileLayer. Tile-service layers are rendered this way — they have no
 * `renderer` property.
 *
 * Returns true if a matching style layer was found and updated.
 */
export function updateVectorTileLayerPaint(
	view: __esri.MapView,
	layerId: string,
	paintUpdates: Record<string, unknown>,
): boolean {
	const vtl = findOwnerVtl(view, layerId);
	if (!vtl) return false;
	const styleLayer = vtl.getStyleLayer(layerId);
	return setVectorTileLayerStyleLayerIfChanged(
		vtl,
		{
			...styleLayer,
			paint: { ...(styleLayer.paint ?? {}), ...paintUpdates },
		},
		{ source: 'updateVectorTileLayerPaint' },
	);
}

/**
 * Returns the correct Mapbox paint color key for a VectorTileLayer style layer
 * (e.g. "line-color", "fill-color").  Returns null if the style layer is not found.
 */
function vectorTileColorKey(
	view: __esri.MapView,
	layerId: string,
): { vtl: any; styleLayer: any; key: string } | null {
	const layers = view.map?.layers?.toArray() ?? [];
	for (const layer of layers) {
		if (!('getStyleLayer' in layer && 'setStyleLayer' in layer)) continue;
		const vtl = layer as any;
		if (!vtl.loaded) continue;
		const styleLayer = vtl.getStyleLayer?.(layerId);
		if (!styleLayer) continue;
		if (styleLayer.type === 'symbol') {
			return null;
		}
		const key = (() => {
			switch (styleLayer.type) {
				case 'fill':
					return 'fill-color';
				case 'circle':
					return 'circle-color';
				default:
					return 'line-color';
			}
		})();
		return { vtl, styleLayer, key };
	}
	return null;
}

/**
 * Test hook: returns the current paint colour (or colour expression, if
 * data-driven) for a line/fill VectorTileLayer style layer — e.g. the pipe
 * colour for a "line" layer. Returns undefined for symbol (icon) layers; use
 * {@link getVtlIconSpriteColor} for those instead.
 */
export function getVtlPaintColor(
	view: __esri.MapView,
	layerId: string,
): unknown | undefined {
	const hit = vectorTileColorKey(view, layerId);
	if (!hit) return undefined;
	return hit.styleLayer.paint?.[hit.key];
}

/**
 * Test hook: returns the raw `layout['icon-image']` value for a symbol VTL
 * style layer — a fixed sprite id string, or (for colour-by-attribute/range
 * layers) a `match`/`step` expression keyed on a feature attribute whose
 * leaves are per-band recoloured sprite ids. Unlike {@link getVtlIconSpriteColor}
 * (which only reads a static suffix), this exposes the full expression so a
 * caller can resolve it against a specific feature's attributes to get that
 * feature's actual rendered colour.
 */
export function getVtlRawIconImage(
	view: __esri.MapView,
	layerId: string,
): unknown | undefined {
	const mapLayers = view.map?.layers?.toArray() ?? [];
	for (const layer of mapLayers) {
		if (!('getStyleLayer' in layer && 'setStyleLayer' in layer)) continue;
		const vtl = layer as any;
		if (!vtl.loaded) continue;
		const styleLayer = vtl.getStyleLayer?.(layerId);
		if (!styleLayer) continue;
		return styleLayer.layout?.['icon-image'];
	}
	return undefined;
}

/**
 * Applies any Mapbox color expression (string, step, match, etc.) to a
 * VectorTileLayer style layer, picking the correct paint key automatically.
 * Returns true if a matching style layer was found and updated.
 */
export function setVectorTileColorExpression(
	view: __esri.MapView,
	layerId: string,
	colorExpression: unknown,
): boolean {
	const hit = vectorTileColorKey(view, layerId);
	if (!hit) return false;
	const { vtl, styleLayer, key } = hit;
	return setVectorTileLayerStyleLayerIfChanged(
		vtl,
		{
			...styleLayer,
			paint: { ...(styleLayer.paint ?? {}), [key]: colorExpression },
		},
		{ paintKey: key, source: 'setVectorTileColorExpression' },
	);
}

/**
 * Sets the solid fill/stroke color on a VectorTileLayer style layer.
 * Automatically picks the correct paint key based on the style layer type.
 * Returns true if updated.
 */
export function setVectorTileColor(
	view: __esri.MapView,
	layerId: string,
	color: string,
): boolean {
	return setVectorTileColorExpression(view, layerId, color);
}

/** Default ArcGIS Online vector basemap font glyphs template (MapLibre `{fontstack}` / `{range}` tokens). */
export const WORLD_BASEMAP_V2_GLYPHS_URL =
	'https://basemaps.arcgis.com/arcgis/rest/services/World_Basemap_v2/VectorTileServer/resources/fonts/{fontstack}/{range}.pbf';

const hasTextStyleKeys = (layer: unknown): boolean => {
	if (!layer || typeof layer !== 'object') return false;
	const { layout, paint } = layer as {
		layout?: Record<string, unknown>;
		paint?: Record<string, unknown>;
	};
	const hasTextKey = (obj: Record<string, unknown> | undefined) => {
		if (!obj) return false;
		return Object.keys(obj).some(
			key => key === 'text-field' || key.startsWith('text-'),
		);
	};
	return hasTextKey(layout) || hasTextKey(paint);
};

/**
 * Normalizes Esri-friendly text layout/paint on a single MapLibre-style layer when it uses
 * any `text-*` keys. Mutates `layer` in place. Returns whether this layer had text styling.
 */
function normalizeVectorTileTextStyleLayer(layer: unknown): boolean {
	if (!hasTextStyleKeys(layer)) return false;

	const styleLayer = layer as {
		layout?: Record<string, unknown>;
		paint?: Record<string, unknown>;
	};
	const originalLayout = { ...(styleLayer.layout ?? {}) };
	const originalPaint = styleLayer.paint ?? {};
	const preservedTextColor =
		(originalPaint as Record<string, unknown>)['text-color'] ?? '#000000';

	// remove all text-* keys from the layout & paint

	// Esri does not have the same support for all the Mapbox text-* keys
	// for example, text-opacity with step is not supported: [ "step", ['zoom'], 0, 20, 1 ]
	Object.keys(originalLayout).forEach((key: string) => {
		if (key !== 'text-field' && key.startsWith('text-')) {
			delete originalLayout[key];
		}
	});

	Object.keys(originalPaint).forEach((key: string) => {
		if (key !== 'text-field' && key.startsWith('text-')) {
			delete originalPaint[key];
		}
	});

	// add the basic text-* keys that are supported by Esri
	styleLayer.layout = {
		...originalLayout,
		'text-font': ['Arial Unicode MS Regular'],
		'text-size': 12,
		'text-offset': [2, 2],
	};

	styleLayer.paint = {
		...(typeof originalPaint === 'object' && originalPaint !== null
			? originalPaint
			: {}),
		'text-color': preservedTextColor,
	};

	return true;
}

/**
 * For every layer in `style.layers` that uses text, normalizes layout/paint for glyph rendering,
 * then sets `style.glyphs` to the World Basemap v2 font URL template when any text layer exists.
 * Mutates the passed `style` object (including nested layer objects).
 *
 * In short update `text-*` properties to be supported by Esri for Text Labels on the map.
 */
export function updateStlyeLabelConfig(
	style: VectorTileLayerProperties['style'],
	glyphsUrl: string = WORLD_BASEMAP_V2_GLYPHS_URL,
) {
	if (!style || typeof style !== 'object') return;
	const layers = (style as { layers?: unknown[] }).layers;
	if (!Array.isArray(layers)) return;

	let hasAnyTextLayer = false;
	for (const lyr of layers) {
		if (normalizeVectorTileTextStyleLayer(lyr)) {
			hasAnyTextLayer = true;
		}
	}
	if (hasAnyTextLayer) {
		(style as { glyphs?: string }).glyphs = glyphsUrl;
	}
}

// --- SymbolLoader raster / sprite packing (Mapbox → Esri renderer) ---

/**
 * Legacy SVG data URL from bundled icons map (fallback when raster load fails).
 */
export function getIconDataUrl(icon: string): string | null {
	const iconName = (() => {
		const regexName = /(?<=:)(.*?)(?=~)/g;
		const nameMatch = icon.match(regexName);
		return nameMatch ? nameMatch[0] : icon;
	})();
	const iconColor = (() => {
		const regexColor = /([a-f0-9]{6}|[a-f0-9]{3})$/gi;
		const colorMatch = icon.match(regexColor);
		return colorMatch?.length ? colorMatch[0] : null;
	})();
	if (iconName && icons[iconName]) {
		return iconColor
			? icons[iconName].replace(
					/fill='%23([a-f0-9]{6}|[a-f0-9]{3})'/gi,
					`fill='%23${iconColor}'`,
				)
			: icons[iconName];
	}
	return null;
}

/** Converts SymbolLoader raster ({@link SymbolBufferImage}) or loaded image to PNG data URL. */
function symbolRasterToDataUrl(
	image: HTMLImageElement | SymbolBufferImage,
): string {
	if (
		'data' in image &&
		image.data &&
		image.data.length > 0 &&
		image.width > 0 &&
		image.height > 0
	) {
		const arr = new Uint8ClampedArray(image.data);
		const imageData = new ImageData(arr, image.width, image.height);
		const canvas = document.createElement('canvas');
		canvas.width = image.width;
		canvas.height = image.height;
		const ctx = canvas.getContext('2d');
		if (!ctx) {
			return '';
		}
		ctx.putImageData(imageData, 0, 0);
		return canvas.toDataURL('image/png');
	}
	const img = image as HTMLImageElement;
	const w = img.naturalWidth || img.width || 24;
	const h = img.naturalHeight || img.height || 24;
	const canvas = document.createElement('canvas');
	canvas.width = w;
	canvas.height = h;
	const ctx = canvas.getContext('2d');
	if (!ctx) {
		return '';
	}
	ctx.drawImage(img, 0, 0);
	return canvas.toDataURL('image/png');
}

function getSymbolImageAsync(
	loader: SymbolLoader,
	symbolId: string,
): Promise<HTMLImageElement | SymbolBufferImage> {
	return new Promise((resolve, reject) => {
		try {
			loader.getImage(symbolId, image => {
				resolve(image);
			});
		} catch (e) {
			reject(e);
		}
	});
}

/**
 * Raster from {@link SymbolLoader#getImage} as a PNG data URL for
 * {@link PictureMarkerSymbol}.
 */
export async function pictureDataUrlFromSymbolLoader(
	loader: SymbolLoader,
	iconRef: string,
): Promise<string> {
	const symbolId = iconRef.includes(SymbolLoader.namespace)
		? iconRef
		: SymbolLoader.createSymbolId(iconRef);
	try {
		const image = await getSymbolImageAsync(loader, symbolId);
		const dataUrl = symbolRasterToDataUrl(image);
		if (dataUrl) {
			return dataUrl;
		}
	} catch {
		// fall through to legacy SVG data URL
	}
	return getIconDataUrl(iconRef) ?? '';
}

/**
 * Draws a SymbolLoader raster (BufferImage with RGBA or HTMLImageElement) into
 * the context, stretched to dw×dh pixels.
 */
function drawSymbolImageScaled(
	ctx: CanvasRenderingContext2D,
	image: HTMLImageElement | SymbolBufferImage,
	dx: number,
	dy: number,
	dw: number,
	dh: number,
): void {
	if (
		'data' in image &&
		image.data &&
		image.data.length > 0 &&
		image.width > 0 &&
		image.height > 0
	) {
		const { width, height, data } = image;
		const arr = new Uint8ClampedArray(data);
		const imageData = new ImageData(arr, width, height);
		const c = document.createElement('canvas');
		c.width = width;
		c.height = height;
		const cctx = c.getContext('2d');
		if (!cctx) {
			throw new Error('Could not get 2d context for symbol temp canvas');
		}
		cctx.putImageData(imageData, 0, 0);
		ctx.drawImage(c, dx, dy, dw, dh);
	} else {
		const img = image as HTMLImageElement;
		ctx.drawImage(img, dx, dy, dw, dh);
	}
}

/**
 * Builds a 1x or 2x sprite sheet from SymbolLoader raster output (imageData /
 * canvas) for the given canonical sprite ids (`inno:…`). Only the atlas
 * matching `pixelRatio` is populated; the other resolution returns empty JSON
 * and a 1×1 placeholder canvas.
 */
export async function packSpriteFromSymbolLoader(
	loader: SymbolLoader,
	iconIds: string[],
	pixelRatio: 1 | 2,
	maxRowWidth = 4096,
	margin = 1,
): Promise<{
	json1x: Record<string, SpriteInfo>;
	image1x: HTMLCanvasElement;
	json2x: Record<string, SpriteInfo>;
	image2x: HTMLCanvasElement;
}> {
	const emptyPlaceholderCanvas = (): HTMLCanvasElement => {
		const c = document.createElement('canvas');
		c.width = 1;
		c.height = 1;
		return c;
	};

	const uniqueIds = [...new Set(iconIds)].sort();
	if (uniqueIds.length === 0) {
		const empty = emptyPlaceholderCanvas();
		return {
			json1x: {},
			image1x: empty,
			json2x: {},
			image2x: emptyPlaceholderCanvas(),
		};
	}

	const loaded = await Promise.all(
		uniqueIds.map(async id => {
			const image = await getSymbolImageAsync(loader, id);
			let w: number;
			let h: number;
			if ('data' in image && image.data && image.data.length > 0) {
				w = image.width;
				h = image.height;
			} else {
				const img = image as HTMLImageElement;
				w = img.naturalWidth || img.width || 32;
				h = img.naturalHeight || img.height || 32;
			}
			return { id, image, w, h };
		}),
	);

	type Dim = { id: string; w: number; h: number };
	const layoutRow = (
		dims: Dim[],
		rowMax: number,
		m: number,
	): {
		placements: Array<Dim & { dstX: number; dstY: number }>;
		canvasW: number;
		canvasH: number;
	} => {
		const placements: Array<Dim & { dstX: number; dstY: number }> = [];
		let x = 0;
		let y = 0;
		let rowH = 0;
		for (const d of dims) {
			const { w, h } = d;
			if (x > 0 && x + w + m > rowMax) {
				x = 0;
				y += rowH + m;
				rowH = 0;
			}
			placements.push({ ...d, dstX: x, dstY: y });
			x += w + m;
			rowH = Math.max(rowH, h);
		}
		const canvasW = placements.reduce(
			(acc, p) => Math.max(acc, p.dstX + p.w),
			0,
		);
		const canvasH = placements.reduce(
			(acc, p) => Math.max(acc, p.dstY + p.h),
			0,
		);
		return { placements, canvasW, canvasH };
	};

	const byId = new Map(loaded.map(e => [e.id, e]));

	let json1x: Record<string, SpriteInfo> = {};
	let image1x: HTMLCanvasElement = emptyPlaceholderCanvas();
	let json2x: Record<string, SpriteInfo> = {};
	let image2x: HTMLCanvasElement = emptyPlaceholderCanvas();

	if (pixelRatio === 1) {
		const dims1x: Dim[] = loaded.map(({ id, w, h }) => ({ id, w, h }));
		const {
			placements: p1,
			canvasW: cw1,
			canvasH: ch1,
		} = layoutRow(dims1x, maxRowWidth, margin);

		const canvas1x = document.createElement('canvas');
		canvas1x.width = cw1;
		canvas1x.height = ch1;
		const ctx1 = canvas1x.getContext('2d');
		if (!ctx1) {
			throw new Error('Could not get 2d context for 1x sprite canvas');
		}

		json1x = {};
		for (const p of p1) {
			const entry = byId.get(p.id);
			if (!entry) continue;
			drawSymbolImageScaled(ctx1, entry.image, p.dstX, p.dstY, p.w, p.h);
			json1x[p.id] = {
				x: p.dstX,
				y: p.dstY,
				width: p.w,
				height: p.h,
				pixelRatio: 1,
			};
		}
		image1x = canvas1x;
	} else {
		const dims2x: Dim[] = loaded.map(({ id, w, h }) => ({
			id,
			w: w * 2,
			h: h * 2,
		}));
		const {
			placements: p2,
			canvasW: cw2,
			canvasH: ch2,
		} = layoutRow(dims2x, maxRowWidth * 2, margin * 2);

		const canvas2x = document.createElement('canvas');
		canvas2x.width = cw2;
		canvas2x.height = ch2;
		const ctx2 = canvas2x.getContext('2d');
		if (!ctx2) {
			throw new Error('Could not get 2d context for 2x sprite canvas');
		}

		json2x = {};
		for (const p of p2) {
			const entry = byId.get(p.id);
			if (!entry) continue;
			drawSymbolImageScaled(ctx2, entry.image, p.dstX, p.dstY, p.w, p.h);
			json2x[p.id] = {
				x: p.dstX,
				y: p.dstY,
				width: p.w,
				height: p.h,
				pixelRatio: 2,
			};
		}
		image2x = canvas2x;
	}

	return {
		json1x,
		image1x,
		json2x,
		image2x,
	};
}

// ---------------------------------------------------------------------------
// Icon-image color patching for VectorTileLayer symbol layers
// ---------------------------------------------------------------------------

/**
 * Replaces the icon-name segment of an `icon-image` expression with
 * `newIconName`, keeping the existing color/selected suffixes intact.
 *
 * Handles two forms:
 *  - Fixed string  `"inno:manhole~#aabbcc"` → `"inno:newIcon~#aabbcc"`
 *  - Data-driven concat `["concat","inno:",["get","type"],"~#aabbcc"]`
 *    → collapses to the fixed string `"inno:newIcon~#aabbcc"` because the
 *    icon is now overridden per-layer rather than per-feature.
 *
 * Non-inno strings and unrecognised expressions are returned unchanged.
 */
export function replaceIconNameInIconImageExpr(
	expr: unknown,
	newIconName: string,
): unknown {
	if (typeof expr === 'string') {
		const prefix = SymbolLoader.namespace; // 'inno:'
		if (!expr.startsWith(prefix)) return expr;
		const afterPrefix = expr.slice(prefix.length);
		const tildeIdx = afterPrefix.indexOf('~');
		// Preserve everything from the first '~' onwards (color + selected suffix).
		const suffix = tildeIdx >= 0 ? afterPrefix.slice(tildeIdx) : '';
		return `${prefix}${newIconName}${suffix}`;
	}
	if (!Array.isArray(expr)) return expr;
	// For a concat expression, extract the color suffix element (starts with '~')
	// and collapse the whole expression to a fixed string.
	if (expr[0] === 'concat') {
		const colorEl = expr.find(
			(e: unknown) =>
				typeof e === 'string' && (e as string).startsWith('~'),
		) as string | undefined;
		const suffix = colorEl ?? '';
		return `${SymbolLoader.namespace}${newIconName}${suffix}`;
	}
	return expr.map((item: unknown) =>
		replaceIconNameInIconImageExpr(item, newIconName),
	);
}

/**
 * Adds or removes the `-nobackground` suffix from a Mapbox `icon-image`
 * expression (string or nested array).
 */
export function toggleBackgroundInIconImageExpr(
	expr: unknown,
	disableBackground: boolean,
): unknown {
	if (typeof expr === 'string') {
		const suffix = SymbolLoader.disableBackgroundPosfix; // '-nobackground'
		const hasSuffix = expr.includes(suffix);
		if (disableBackground && !hasSuffix) return `${expr}${suffix}`;
		if (!disableBackground && hasSuffix) return expr.replace(suffix, '');
		return expr;
	}
	if (!Array.isArray(expr)) return expr;
	return expr.map(item =>
		toggleBackgroundInIconImageExpr(item, disableBackground),
	);
}

/**
 * Updates `layout['icon-image']` on a VTL symbol style layer and its known
 * variants (`-selected`, `-highlight-background`, `-highlight-foreground`) in
 * a single pass. Applies all requested transforms — iconName, color,
 * disableBackground — in order; omit any to leave it unchanged.
 *
 * Returns `true` if at least one style layer was updated.
 */
export function updateVtlIconImage(
	view: MapView,
	layerId: string,
	opts: { color?: string; iconName?: string; disableBackground?: boolean },
): boolean {
	const vtl = findOwnerVtl(view, layerId);
	if (!vtl) return false;
	let updated = false;
	for (const id of vtlStyleVariantIds(layerId)) {
		const styleLayer = vtl.getStyleLayer?.(id);
		if (!styleLayer || styleLayer.type !== 'symbol') continue;
		let iconImage: unknown = styleLayer.layout?.['icon-image'];
		if (iconImage === undefined) continue;
		const original = iconImage;
		if (opts.iconName !== undefined)
			iconImage = replaceIconNameInIconImageExpr(
				iconImage,
				opts.iconName,
			);
		if (opts.color !== undefined)
			iconImage = replaceColorInIconImageExpr(iconImage, opts.color);
		if (opts.disableBackground !== undefined)
			iconImage = toggleBackgroundInIconImageExpr(
				iconImage,
				opts.disableBackground,
			);
		if (JSON.stringify(original) === JSON.stringify(iconImage)) continue;
		if (
			setVectorTileLayerStyleLayerIfChanged(
				vtl,
				{
					...styleLayer,
					layout: {
						...(styleLayer.layout ?? {}),
						'icon-image': iconImage,
					},
				},
				{ source: 'updateVtlIconImage' },
			)
		) {
			updated = true;
		}
	}
	return updated;
}

/**
 * Finds the first `inno:` sprite-id string anywhere in an `icon-image` value
 * (plain string, or nested `concat`/`match`/`step` expression). Used as the
 * recolour template so re-applying a data-driven colour-by always rebuilds from
 * a single clean sprite id rather than a previously-applied match expression.
 */
function firstInnoSprite(expr: unknown): string | undefined {
	if (typeof expr === 'string') {
		return expr.includes(SymbolLoader.namespace) ? expr : undefined;
	}
	if (Array.isArray(expr)) {
		for (const item of expr) {
			const found = firstInnoSprite(item);
			if (found) return found;
		}
	}
	return undefined;
}

/**
 * Colours a *symbol* (icon) VectorTileLayer style layer by value/range. Symbol
 * layers have no paint colour key (`setVectorTileColorExpression` returns false
 * for them), so instead of a paint colour we set `layout['icon-image']` to a
 * data-driven expression that swaps the icon sprite per range/value, recolouring
 * the layer's existing sprite. Every referenced `inno:<icon>~<colour>` sprite is
 * collected and rasterised by the sprite refresh the caller schedules, so icons
 * never point at a missing sprite (which would blank them).
 *
 * Returns true if the base style layer's icon-image was updated.
 */
export function setVtlSymbolColorByExpression(
	view: MapView,
	layerId: string,
	colorExpr: unknown,
): boolean {
	const plan = buildVtlSymbolColorByPlan(view, layerId, colorExpr);
	if (!plan) return false;
	return applyVtlStyleLayer(view, layerId, plan.nextStyleLayer);
}

/**
 * Computes — but does NOT apply — the data-driven `icon-image` style layer for a
 * symbol VTL colour-by. The returned `nextStyleLayer` is the symbol style layer
 * with `layout['icon-image']` overwritten by the recoloured per-band expression.
 *
 * Splitting compute from apply lets callers build the sprite atlas for the
 * target band sprites *before* the live `icon-image` references them — so icons
 * transition sprite→sprite with no blank frame. The `nextStyleLayer` doubles as
 * the synthetic layer fed to the atlas build, since
 * `collectSpriteIconIdsFromStyleLayers` walks `layout['icon-image']`.
 *
 * Returns null when the layer is not a symbol layer, has no `inno:` base sprite,
 * or the expression is unchanged (no-op).
 */
export function buildVtlSymbolColorByPlan(
	view: MapView,
	layerId: string,
	colorExpr: unknown,
	options?: {
		/**
		 * Use this sprite id as the recolour template instead of the layer's
		 * current icon — e.g. `'inno:circle~#768d95-nobackground'` to render
		 * sensors as colour-by "bubbles" (a backgroundless glow disc) instead of
		 * their asset icon. The colour is replaced per band; any trailing suffix
		 * (`-nobackground`, `-selected`) is preserved.
		 */
		baseSpriteOverride?: string;
		/** Extra `layout` props merged onto the style layer (e.g. bubble sizing). */
		extraLayout?: Record<string, unknown>;
		/** Extra `paint` props merged onto the style layer (e.g. icon-opacity). */
		extraPaint?: Record<string, unknown>;
	},
): { nextStyleLayer: any } | null {
	const vtl = findOwnerVtl(view, layerId);
	if (!vtl) return null;
	const styleLayer = vtl.getStyleLayer?.(layerId);
	if (!styleLayer || styleLayer.type !== 'symbol') return null;
	const current = styleLayer.layout?.['icon-image'];
	if (current === undefined) return null;
	// Rebuild from a single clean sprite id so repeated colour-by edits don't
	// compound a previously-applied match expression.
	const baseSprite: unknown =
		options?.baseSpriteOverride ??
		firstInnoSprite(current) ??
		(typeof current === 'string' ? current : undefined);
	if (!baseSprite) return null;
	const iconImage = colorExpressionToIconImageExpression(
		colorExpr,
		baseSprite as string,
	);
	const nextLayout = {
		...(styleLayer.layout ?? {}),
		'icon-image': iconImage,
		...(options?.extraLayout ?? {}),
	};
	const nextPaint = {
		...(styleLayer.paint ?? {}),
		...(options?.extraPaint ?? {}),
	};
	const next = { ...styleLayer, layout: nextLayout, paint: nextPaint };
	if (
		JSON.stringify(styleLayer.layout) === JSON.stringify(nextLayout) &&
		JSON.stringify(styleLayer.paint ?? {}) === JSON.stringify(nextPaint)
	)
		return null;
	return { nextStyleLayer: next };
}

/**
 * Applies a pre-built symbol style layer (from {@link buildVtlSymbolColorByPlan})
 * to its owning VTL. Returns true if the layer changed.
 */
export function applyVtlStyleLayer(
	view: MapView,
	layerId: string,
	nextStyleLayer: any,
): boolean {
	const vtl = findOwnerVtl(view, layerId);
	if (!vtl) return false;
	return setVectorTileLayerStyleLayerIfChanged(vtl, nextStyleLayer, {
		source: 'applyVtlSymbolColorBy',
	});
}

// ---------------------------------------------------------------------------
// Sensor heatmap "bubble" glow — shared construction
// ---------------------------------------------------------------------------

/**
 * `layout` overrides for a sensor symbol layer rendered as a heatmap "bubble":
 * larger, zoom-responsive glow discs. `scale` is the Theme Editor "Circle scale"
 * slider (Mapbox default 3); it multiplies the icon size so the glow grows/shrinks
 * with the slider like the Mapbox circle radius. Opacity is a *paint* property
 * (see {@link bubbleSymbolPaint}) — NOT layout — so it lives separately.
 */
export function bubbleSymbolLayout(scale?: number): Record<string, unknown> {
	const m = (typeof scale === 'number' && scale > 0 ? scale : 3) / 3;
	return {
		'icon-size': [
			'interpolate',
			['linear'],
			['zoom'],
			2,
			0.8 * m,
			13,
			1.6 * m,
			18,
			3 * m,
		],
		'icon-allow-overlap': true,
	};
}

/**
 * `paint` overrides for a bubble. `icon-opacity` is a Mapbox *paint* property
 * (placing it in `layout` is silently ignored by the VTL renderer), driven by
 * the Theme Editor "Fill opacity" slider (`fillOpacity`, 0–1). The glow disc
 * sprite already fades at its edges; this scales overall translucency.
 */
export function bubbleSymbolPaint(fillOpacity?: number): Record<string, unknown> {
	return {
		'icon-opacity': typeof fillOpacity === 'number' ? fillOpacity : 0.8,
	};
}

/**
 * Builds the "bubble" glow form of a sensor symbol style layer: the layer with
 * its `icon-image` replaced by `glowSprite` (a backgroundless glow disc id) plus
 * the bubble layout/paint. Used to bake the glow into the VTL style at build
 * time (so the asset icon never flickers through) and is the uniform-colour base
 * the per-band colour-by recolours on top of.
 */
export function bubbleGlowStyleLayer(
	styleLayer: Record<string, any>,
	glowSprite: string,
	scale?: number,
	fillOpacity?: number,
): Record<string, any> {
	return {
		...styleLayer,
		layout: {
			...(styleLayer.layout ?? {}),
			'icon-image': glowSprite,
			...bubbleSymbolLayout(scale),
		},
		paint: {
			...(styleLayer.paint ?? {}),
			...bubbleSymbolPaint(fillOpacity),
		},
	};
}

/**
 * Sets the `minzoom` and/or `maxzoom` properties on a VTL style layer and its
 * known variant layers (`-selected`, `-highlight-*`).
 *
 * VTL style layers use Mapbox zoom levels (0-24), NOT Esri scale values.
 * Pass `null` to leave a bound unchanged.
 *
 * Returns `true` if at least one style layer was updated.
 */
export function updateVectorTileLayerZoom(
	view: MapView,
	layerId: string,
	minZoom: number | null,
	maxZoom: number | null,
): boolean {
	const vtl = findOwnerVtl(view, layerId);
	if (!vtl) return false;
	let updated = false;
	for (const id of vtlStyleVariantIds(layerId)) {
		const styleLayer = vtl.getStyleLayer?.(id);
		if (!styleLayer) continue;
		const patch: Record<string, unknown> = { ...styleLayer };
		if (minZoom !== null) patch.minzoom = minZoom;
		if (maxZoom !== null) patch.maxzoom = maxZoom;
		if (
			setVectorTileLayerStyleLayerIfChanged(vtl, patch, {
				source: 'updateVectorTileLayerZoom',
			})
		) {
			updated = true;
		}
	}
	return updated;
}
